import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FileText, Users, CurrencyEur, ChartLine, Clock, Bell,
  Warning, ArrowRight, Check, TrendUp, Calendar, Sparkle
} from '@phosphor-icons/react';

import { API } from '../config/api';

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, todayRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/dashboard/today`)
        ]);
        setStats(statsRes.data || {});
        setToday(todayRes.data || {});
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const locale = i18n.language === 'es' ? 'es-ES' : 'it-IT';

  const formatCurrency = (val) => {
    return (val || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isOverdue = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  const kpis = [
    { label: t('dashboard.totalQuotes'), value: stats?.total_quotes || 0, icon: FileText, color: 'text-[#0033A0]', bgColor: 'bg-blue-50', sub: `${stats?.quotes_this_month || 0} ${i18n.language === 'es' ? 'este mes' : 'questo mese'}` },
    { label: t('dashboard.revenue'), value: `\u20AC${formatCurrency(stats?.total_revenue)}`, icon: CurrencyEur, color: 'text-green-600', bgColor: 'bg-green-50', sub: `\u20AC${formatCurrency(stats?.revenue_this_month)} ${i18n.language === 'es' ? 'este mes' : 'questo mese'}` },
    { label: i18n.language === 'es' ? 'Tasa Conversion' : 'Tasso Conversione', value: `${stats?.conversion_rate || 0}%`, icon: TrendUp, color: 'text-amber-600', bgColor: 'bg-amber-50', sub: `${stats?.won_quotes || 0} ${i18n.language === 'es' ? 'ganados de' : 'vinti su'} ${stats?.total_quotes || 0}` },
    { label: t('nav.clients'), value: stats?.total_clients || 0, icon: Users, color: 'text-purple-600', bgColor: 'bg-purple-50', sub: `${today?.new_leads_today || 0} ${i18n.language === 'es' ? 'nuevos hoy' : 'nuovi oggi'}` },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
            {t('dashboard.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/ai-survey">
            <Button variant="outline" className="border-[#0033A0] text-[#0033A0]">
              <Sparkle size={18} className="mr-2" weight="fill" />
              {t('nav.aiSurvey')}
            </Button>
          </Link>
          <Link to="/quotes/new">
            <Button className="bg-[#0033A0] hover:bg-[#0047AB] text-white">
              <FileText size={18} className="mr-2" />
              {t('quotes.newQuote')}
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border border-[#E2E8F0] rounded-sm" data-testid={`kpi-card-${i}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-[#4A5568] font-medium uppercase tracking-wider">{kpi.label}</p>
                  <p className="text-2xl font-bold text-[#0A0F1A] mt-1">{kpi.value}</p>
                  <p className="text-xs text-[#4A5568] mt-1">{kpi.sub}</p>
                </div>
                <div className={`w-10 h-10 ${kpi.bgColor} rounded-sm flex items-center justify-center`}>
                  <kpi.icon size={20} className={kpi.color} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Today's Activities + Recent Quotes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Attività Oggi */}
        <Card className="border border-[#E2E8F0] rounded-sm">
          <CardHeader className="border-b border-[#E2E8F0] pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
              <Calendar size={18} className="text-[#0033A0]" />
              {i18n.language === 'es' ? 'Actividad Hoy' : 'Attivita Oggi'}
            </CardTitle>
            <Link to="/follow-ups" className="text-xs text-[#0033A0] hover:underline flex items-center gap-1">
              {t('dashboard.viewAll')} <ArrowRight size={12} />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {/* Summary badges */}
            <div className="p-4 flex gap-3 border-b border-[#E2E8F0]">
              {(today?.total_due_followups || 0) > 0 && (
                <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-sm text-xs font-medium flex items-center gap-1.5" data-testid="due-followups-badge">
                  <Bell size={14} />
                  {today.total_due_followups} {i18n.language === 'es' ? 'seguimientos pendientes' : 'follow-up da fare'}
                </span>
              )}
              {(today?.total_expiring || 0) > 0 && (
                <span className="px-3 py-1 bg-red-50 text-red-700 rounded-sm text-xs font-medium flex items-center gap-1.5" data-testid="expiring-badge">
                  <Warning size={14} />
                  {today.total_expiring} {i18n.language === 'es' ? 'presupuestos por vencer' : 'preventivi in scadenza'}
                </span>
              )}
              {(today?.total_due_followups || 0) === 0 && (today?.total_expiring || 0) === 0 && (
                <span className="px-3 py-1 bg-green-50 text-green-700 rounded-sm text-xs font-medium flex items-center gap-1.5">
                  <Check size={14} />
                  {i18n.language === 'es' ? 'Todo en orden!' : 'Tutto in ordine!'}
                </span>
              )}
            </div>

            {/* Due follow-ups */}
            {today?.due_followups?.length > 0 && (
              <div className="divide-y divide-[#E2E8F0]">
                {(today.due_followups || []).slice(0, 5).map(fu => (
                  <div key={fu.id} className="p-3 hover:bg-[#F4F6F8] flex items-center gap-3" data-testid={`followup-item-${fu.id}`}>
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isOverdue(fu.due_date) ? 'bg-red-50' : 'bg-amber-50'}`}>
                      <Clock size={16} className={isOverdue(fu.due_date) ? 'text-red-600' : 'text-amber-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A0F1A] truncate">{fu.message}</p>
                      <p className="text-xs text-[#4A5568]">
                        {fu.client_name || (i18n.language === 'es' ? 'Cliente' : 'Cliente')} | {isOverdue(fu.due_date) ? (i18n.language === 'es' ? 'Vencido' : 'Scaduto') : formatDate(fu.due_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Expiring quotes */}
            {today?.expiring_quotes?.length > 0 && (
              <div className="border-t border-[#E2E8F0]">
                <p className="px-4 pt-3 text-xs font-semibold text-red-600 uppercase tracking-wider">{i18n.language === 'es' ? 'Presupuestos por Vencer' : 'Preventivi in Scadenza'}</p>
                <div className="divide-y divide-[#E2E8F0]">
                  {(today.expiring_quotes || []).map(q => (
                    <Link key={q.id} to={`/quotes/${q.id}`} className="p-3 hover:bg-[#F4F6F8] flex items-center justify-between block" data-testid={`expiring-quote-${q.id}`}>
                      <div>
                        <p className="text-sm font-medium text-[#0A0F1A]">{q.quote_number}</p>
                        <p className="text-xs text-[#4A5568]">{q.client_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-[#0A0F1A]">{'\u20AC'}{formatCurrency(q.total)}</p>
                        <p className="text-xs text-red-600">{q.days_left === 0 ? (i18n.language === 'es' ? 'Vence hoy!' : 'Scade oggi!') : `${q.days_left} ${i18n.language === 'es' ? 'dias' : 'giorni'}`}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {(!today?.due_followups?.length && !today?.expiring_quotes?.length) && (
              <div className="p-6 text-center">
                <Check size={24} className="text-green-600 mx-auto" />
                <p className="text-sm text-[#4A5568] mt-2">{i18n.language === 'es' ? 'Sin actividades urgentes hoy' : 'Nessuna attivita urgente per oggi'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preventivi Recenti */}
        <Card className="border border-[#E2E8F0] rounded-sm">
          <CardHeader className="border-b border-[#E2E8F0] pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
              <FileText size={18} className="text-[#0033A0]" />
              {t('dashboard.recentQuotes')}
            </CardTitle>
            <Link to="/quotes" className="text-xs text-[#0033A0] hover:underline flex items-center gap-1">
              {t('dashboard.viewAll')} <ArrowRight size={12} />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {today?.recent_quotes?.length > 0 ? (
              <div className="divide-y divide-[#E2E8F0]">
                {(today.recent_quotes || []).slice(0, 6).map(q => {
                  const statusColors = {
                    draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
                    won: 'bg-green-100 text-green-700', lost: 'bg-red-100 text-red-700',
                    pending: 'bg-yellow-100 text-yellow-700'
                  };
                  const statusLabels = {
                    draft: t('quotes.draft'), sent: t('quotes.sent'), won: i18n.language === 'es' ? 'Ganado' : 'Vinto', lost: i18n.language === 'es' ? 'Perdido' : 'Perso', pending: i18n.language === 'es' ? 'Pendiente' : 'In attesa'
                  };
                  return (
                    <Link key={q.id} to={`/quotes/${q.id}`} className="p-3 hover:bg-[#F4F6F8] flex items-center justify-between block" data-testid={`recent-quote-${q.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-50 rounded-sm flex items-center justify-center">
                          <FileText size={16} className="text-[#0033A0]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#0A0F1A]">{q.quote_number}</p>
                          <p className="text-xs text-[#4A5568]">{q.client_name || 'N/A'} | {formatDate(q.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-medium ${statusColors[q.status] || 'bg-gray-100'}`}>
                          {statusLabels[q.status] || q.status}
                        </span>
                        <span className="font-semibold text-sm text-[#0A0F1A]">{'\u20AC'}{formatCurrency(q.total)}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center">
                <FileText size={24} className="text-[#E2E8F0] mx-auto" />
                <p className="text-sm text-[#4A5568] mt-2">{t('dashboard.noQuotes')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Overview */}
      <Card className="border border-[#E2E8F0] rounded-sm">
        <CardHeader className="border-b border-[#E2E8F0] pb-3 flex flex-row items-center justify-between">
          <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
            <ChartLine size={18} className="text-[#0033A0]" />
            Opportunità
          </CardTitle>
          <Link to="/pipeline" className="text-xs text-[#0033A0] hover:underline flex items-center gap-1">
            Vedi Opportunità <ArrowRight size={12} />
          </Link>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { key: 'new_lead', label: 'Lead', color: 'bg-gray-200' },
              { key: 'contacted', label: i18n.language === 'es' ? 'Contactado' : 'Contattato', color: 'bg-blue-200' },
              { key: 'site_visit', label: i18n.language === 'es' ? 'Inspeccion' : 'Sopralluogo', color: 'bg-indigo-200' },
              { key: 'quote_sent', label: i18n.language === 'es' ? 'Presupuesto' : 'Preventivo', color: 'bg-purple-200' },
              { key: 'negotiation', label: i18n.language === 'es' ? 'Negociacion' : 'Trattativa', color: 'bg-amber-200' },
              { key: 'won', label: i18n.language === 'es' ? 'Ganado' : 'Vinto', color: 'bg-green-200' },
              { key: 'lost', label: i18n.language === 'es' ? 'Perdido' : 'Perso', color: 'bg-red-200' },
            ].map(stage => (
              <div key={stage.key} className="flex-1 min-w-[90px]">
                <div className={`${stage.color} rounded-sm p-3 text-center`}>
                  <p className="text-xl font-bold text-[#0A0F1A]">{stats?.pipeline_stats?.[stage.key] || 0}</p>
                  <p className="text-[10px] text-[#4A5568] font-medium uppercase tracking-wider mt-0.5">{stage.label}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

